<template>
    <div>
            <base-navigation></base-navigation>

  <div class="loan-dashboard">

    <!-- HEADER -->
    <div class="page-header">
      <h1>Loan Financial Dashboard</h1>
      <p>Overview of loan disbursements and repayments</p>
    </div>


    <!-- SUMMARY CARDS -->
    <div class="summary-grid">

      <div class="card disbursed">
        <div class="card-icon">💰</div>
        <div>
          <h3>Total Disbursed</h3>
          <p>{{ formatCurrency(totalDisbursed) }}</p>
        </div>
      </div>

      <div class="card paid">
        <div class="card-icon">💳</div>
        <div>
          <h3>Total Paid</h3>
          <p>{{ formatCurrency(totalPaid) }}</p>
        </div>
      </div>

      <div class="card interest">
        <div class="card-icon">📈</div>
        <div>
          <h3>Interest Paid</h3>
          <p>{{ formatCurrency(totalInterestPaid) }}</p>
        </div>
      </div>

      <div class="card principal">
        <div class="card-icon">🏦</div>
        <div>
          <h3>Principal Paid</h3>
          <p>{{ formatCurrency(totalPrincipalPaid) }}</p>
        </div>
      </div>

    </div>


    <!-- TABLE -->
    <div class="table-container">

      <div class="table-header">
        <h2>Monthly Financial Summary</h2>
      </div>

      <table>

        <thead>
          <tr>
            <th>Month</th>
            <th>Disbursed</th>
            <th>Paid</th>
            <th>Interest</th>
            <th>Principal</th>
            <th>Outstanding</th>
          </tr>
        </thead>

        <tbody>

          <tr v-for="month in monthlySummary" :key="month.month">

            <td class="month">
              {{ formatMonth(month.month) }}
            </td>

            <td class="disbursed-text">
              {{ formatCurrency(month.disbursed) }}
            </td>

            <td class="paid-text">
              {{ formatCurrency(month.totalPaid) }}
            </td>

            <td class="interest-text">
              {{ formatCurrency(month.interestPaid) }}
            </td>

            <td class="principal-text">
              {{ formatCurrency(month.principalPaid) }}
            </td>

            <td class="outstanding-text">
              {{ formatCurrency(month.outstanding) }}
            </td>

          </tr>

        </tbody>

      </table>

    </div>
        </div>


  </div>
</template>

<script>
import axios from "axios"

export default {

  data() {
    return {
      payments: [],
      loans: []
    }
  },

  async mounted() {

    const paymentsRes = await axios.get(
      "https://api.roberms.com/raisetrack/get/loan/payments"
    )

    const loansRes = await axios.get(
      "https://api.roberms.com/raisetrack/get/loans"
    )

    this.payments = paymentsRes.data
    this.loans = loansRes.data
  },

  computed: {

    totalDisbursed() {
      return this.loans.reduce(
        (sum, l) => sum + Number(l.loan_amount || 0),
        0
      )
    },

    totalPaid() {
      return this.payments.reduce(
        (sum, p) => sum + Number(p.TransAmount || 0),
        0
      )
    },

    totalInterestPaid() {
      return this.payments.reduce(
        (sum, p) => sum + Number(p.interest || 0),
        0
      )
    },

    totalPrincipalPaid() {
      return this.payments.reduce(
        (sum, p) => sum + Number(p.principal || 0),
        0
      )
    },

    monthlySummary() {

      const grouped = {}

      this.payments.forEach(p => {

        const month = new Date(p.payment_date)
          .toISOString()
          .slice(0, 7)

        if (!grouped[month]) {

          grouped[month] = {
            month,
            disbursed: 0,
            totalPaid: 0,
            interestPaid: 0,
            principalPaid: 0,
            outstanding: 0
          }
        }

        grouped[month].totalPaid += Number(p.TransAmount || 0)
        grouped[month].interestPaid += Number(p.interest || 0)
        grouped[month].principalPaid += Number(p.principal || 0)
        grouped[month].outstanding = Number(p.outsandingAmount || 0)

      })

      this.loans.forEach(l => {

        const month = new Date(l.createdAt || l.date_issued)
          .toISOString()
          .slice(0, 7)

        if (!grouped[month]) {

          grouped[month] = {
            month,
            disbursed: 0,
            totalPaid: 0,
            interestPaid: 0,
            principalPaid: 0,
            outstanding: 0
          }
        }

        grouped[month].disbursed += Number(l.loan_amount || 0)

      })

      return Object.values(grouped).sort((a, b) =>
        b.month.localeCompare(a.month)
      )
    }
  },

  methods: {

    formatCurrency(value) {

      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES"
      }).format(value)
    },

    formatMonth(month) {

      const date = new Date(month + "-01")

      return date.toLocaleString("en-KE", {
        month: "long",
        year: "numeric"
      })
    }
  }
}
</script>

<style scoped>

.loan-dashboard {

  padding: 30px;
  background: linear-gradient(135deg, #eef2f7, #f8fafc);
  min-height: 100vh;

}


/* HEADER */

.page-header {

  margin-bottom: 25px;

}

.page-header h1 {

  font-size: 28px;
  font-weight: 700;
  color: #1e293b;

}

.page-header p {

  color: #64748b;

}


/* CARDS */

.summary-grid {

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 30px;

}

.card {

  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border-radius: 14px;
  color: white;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  transition: 0.3s;

}

.card:hover {

  transform: translateY(-6px);

}

.card-icon {

  font-size: 28px;
  background: rgba(255,255,255,0.2);
  padding: 10px;
  border-radius: 10px;

}

.card h3 {

  font-size: 14px;
  opacity: 0.9;

}

.card p {

  font-size: 22px;
  font-weight: bold;

}


/* CARD COLORS */

.disbursed {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.paid {
  background: linear-gradient(135deg, #10b981, #059669);
}

.interest {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.principal {
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
}


/* TABLE */

.table-container {

  background: white;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  overflow: hidden;

}

.table-header {

  padding: 20px;
  border-bottom: 1px solid #eee;

}

.table-header h2 {

  font-size: 18px;
  color: #1e293b;

}

table {

  width: 100%;
  border-collapse: collapse;

}

th {

  padding: 14px;
  background: #f1f5f9;
  text-align: left;
  font-size: 13px;
  color: #475569;

}

td {

  padding: 14px;
  border-bottom: 1px solid #f1f5f9;

}

tr:hover {

  background: #f8fafc;

}


/* COLORS */

.month {

  font-weight: 600;

}

.disbursed-text {

  color: #2563eb;
  font-weight: 600;

}

.paid-text {

  color: #059669;
  font-weight: 600;

}

.interest-text {

  color: #d97706;
  font-weight: 600;

}

.principal-text {

  color: #6d28d9;
  font-weight: 600;

}

.outstanding-text {

  color: #dc2626;
  font-weight: 600;

}

</style>